import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import { track } from "@mydailydirect/app-kit";
import { cartLineKey, parseCartLineKey } from "@mydailydirect/ui";

export interface CartLine {
  skuId: number;
  qty: number;
  packId?: number | null;
}

interface CartContextValue {
  lines: CartLine[];
  /** Returns qty of a specific assortment, or total items of this SKU if packId is omitted. */
  qtyOf: (skuId: number, packId?: number | null) => number;
  setQty: (skuId: number, qty: number, packId?: number | null) => void;
  add: (skuId: number, packId?: number | null) => void;
  clear: () => void;
  setLines: (lines: CartLine[], pushedBy?: string) => void;
  totalItems: number;
  pushedBy?: string;
}

const CartContext = createContext<CartContextValue | null>(null);

const CART_STORAGE_KEY = "mdd_cart_state_v2";
const LEGACY_STORAGE_KEY = "mdd_cart_state";

export function CartProvider({ children }: { children: ReactNode }) {
  const [map, setMap] = useState<Record<string, number>>(() => {
    if (typeof window === "undefined") return {};
    try {
      const raw = localStorage.getItem(CART_STORAGE_KEY);
      if (raw) return JSON.parse(raw);
      // Migrate legacy storage key if present
      const legacyRaw = localStorage.getItem(LEGACY_STORAGE_KEY);
      if (legacyRaw) {
        const parsed = JSON.parse(legacyRaw);
        const migrated: Record<string, number> = {};
        for (const [k, v] of Object.entries(parsed)) {
          if (typeof v === "number" && v > 0) migrated[String(k)] = v;
        }
        return migrated;
      }
      return {};
    } catch {
      return {};
    }
  });
  const [pushedBy, setPushedBy] = useState<string | undefined>();

  // Synchronize cart state to localStorage
  useEffect(() => {
    if (typeof window === "undefined") return;
    try {
      localStorage.setItem(CART_STORAGE_KEY, JSON.stringify(map));
    } catch {}
  }, [map]);

  const setQty = useCallback((skuId: number, qty: number, packId?: number | null) => {
    const key = cartLineKey(skuId, packId);
    setMap((prev) => {
      const current = prev[key] ?? 0;
      if (qty > current) {
        track("add_to_cart", { skuId, propsJson: { qty, packId } });
      }
      const next = { ...prev };
      if (qty <= 0) delete next[key];
      else next[key] = qty;
      return next;
    });
  }, []);

  const add = useCallback((skuId: number, packId?: number | null) => {
    const key = cartLineKey(skuId, packId);
    track("add_to_cart", { skuId, propsJson: { packId } });
    setMap((prev) => ({ ...prev, [key]: (prev[key] ?? 0) + 1 }));
  }, []);

  const qtyOf = useCallback((skuId: number, packId?: number | null): number => {
    if (packId !== undefined) {
      return map[cartLineKey(skuId, packId)] ?? 0;
    }
    // Sum across all assortments for this SKU
    let sum = 0;
    for (const [k, v] of Object.entries(map)) {
      const parsed = parseCartLineKey(k);
      if (parsed.skuId === skuId) sum += v;
    }
    return sum;
  }, [map]);

  const setLines = useCallback((lines: CartLine[], pushedByAuthor?: string) => {
    const next: Record<string, number> = {};
    for (const l of lines) {
      if (l.qty > 0) next[cartLineKey(l.skuId, l.packId)] = l.qty;
    }
    setMap(next);
    if (pushedByAuthor) setPushedBy(pushedByAuthor);
  }, []);

  const clear = useCallback(() => {
    setMap({});
    setPushedBy(undefined);
  }, []);

  const value = useMemo<CartContextValue>(() => {
    const lines = Object.entries(map).map(([key, qty]) => {
      const { skuId, packId } = parseCartLineKey(key);
      return { skuId, qty, packId };
    });
    return {
      lines,
      qtyOf,
      setQty,
      add,
      clear,
      setLines,
      totalItems: lines.reduce((sum, l) => sum + l.qty, 0),
      pushedBy,
    };
  }, [map, qtyOf, setQty, add, clear, setLines, pushedBy]);

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
}

export function useCart(): CartContextValue {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error("useCart must be used within a CartProvider");
  return ctx;
}
